import { useState } from "react";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCleanerPastCleanings, type PastCleaning } from "@/hooks/useCleanerPastCleanings";
import { JobEvidenceView } from "@/components/owner/JobEvidenceView";
import { useAuth } from "@/contexts/AuthContext";
import { formatPersonLabel } from "@/lib/taskAssigneeLabel";

function IssueChips({ row }: { row: PastCleaning }) {
  if (row.issue_missing === 0 && row.issue_broken === 0) {
    return <span className="text-[13px] font-medium text-[#94a3b8]">None</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {row.issue_missing > 0 ? (
        <span className="inline-flex rounded-[9px] bg-[#fdf3e5] px-3 py-1.5 text-[13px] font-bold text-[#b45309]">
          {row.issue_missing === 1 ? "Missing · 1" : `Missing · ${row.issue_missing}`}
        </span>
      ) : null}
      {row.issue_broken > 0 ? (
        <span className="inline-flex rounded-[9px] bg-[#fbeae8] px-3 py-1.5 text-[13px] font-bold text-[#b4453a]">
          {row.issue_broken === 1 ? "Broken · 1" : `Broken · ${row.issue_broken}`}
        </span>
      ) : null}
    </div>
  );
}

export function PastCleaningsTab({
  onEvidenceOpenChange,
}: {
  onEvidenceOpenChange?: (open: boolean) => void;
} = {}) {
  const { profile } = useAuth();
  const { data: pastCleanings, isLoading } = useCleanerPastCleanings();
  const [openId, setOpenId] = useState<string | null>(null);

  const setOpen = (id: string | null) => {
    setOpenId(id);
    onEvidenceOpenChange?.(!!id);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16 text-[#64748b]">
        <Loader2 className="h-7 w-7 animate-spin" />
      </div>
    );
  }

  const openRow = pastCleanings?.find((r) => r.id === openId) ?? null;

  if (openRow) {
    const cleanerName =
      formatPersonLabel(openRow.cleaner_name || profile?.full_name || "") ||
      openRow.cleaner_name ||
      "You";
    return (
      <JobEvidenceView
        mode="cleaner"
        propertyId={openRow.property_id}
        propertyLabel={openRow.property_name}
        submittedAt={openRow.submitted_at}
        cleanerName={cleanerName}
        cleaningTask={openRow.cleaning_task}
        cleaningTaskId={openRow.cleaning_task_id}
        onBack={() => setOpen(null)}
      />
    );
  }

  if (!pastCleanings || pastCleanings.length === 0) {
    return (
      <div
        className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-8 pb-8 text-center"
        data-tour="cleaner-past-empty"
      >
        <div className="flex h-[78px] w-[78px] items-center justify-center rounded-[22px] bg-[#eef3f6]">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="8.5" />
            <path d="M12 7.5V12l3 2" />
          </svg>
        </div>
        <div className="flex flex-col gap-[7px]">
          <div className="text-[27px] font-bold tracking-[-0.6px] text-[#0f1720]">No past jobs</div>
          <div className="text-[15px] font-medium leading-[1.45] text-[#64748b]">
            Cleans you finish will be saved here.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-tour="cleaner-past-list">
      {pastCleanings.map((row) => {
        const dateLabel = format(new Date(row.submitted_at), "MMM d");
        return (
          <div
            key={row.id}
            className="flex flex-col gap-[11px] rounded-2xl border-[1.5px] border-[#e6edf1] bg-white px-4 py-[15px]"
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[17px] font-bold tracking-[-0.3px] text-[#0f1720]">
                  {row.property_name}
                </div>
                <div className="mt-0.5 text-[14px] font-medium text-[#64748b]">
                  Completed {dateLabel}
                  {row.photo_count > 0 ? ` · ${row.photo_count} photos` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(row.id)}
                className="flex h-11 shrink-0 items-center rounded-[13px] bg-[#24a89a] px-5 text-[15px] font-bold text-white hover:bg-[#1f958a]"
              >
                Open
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  "inline-flex rounded-[9px] px-3 py-1.5 text-[13px] font-bold",
                  row.keys_label === "Keys left"
                    ? "bg-[#eaf6f4] text-[#15776d]"
                    : "bg-[#eef3f6] text-[#5b6b7a]",
                )}
              >
                {row.keys_label}
              </span>
              <IssueChips row={row} />
              <span className="inline-flex rounded-[9px] bg-[#e3efe5] px-3 py-1.5 text-[13px] font-bold text-[#2f7a45]">
                Done
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
